import React, { useState, useEffect, useRef } from 'react';
import Glyph from '../glyphs/Glyph';

interface CatchItemsGameProps {
  onComplete: (score: number) => void;
}

export default function CatchItemsGame({ onComplete }: CatchItemsGameProps) {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [items, setItems] = useState<{id: number, x: number, y: number, type: 'star' | 'bomb'}[]>([]);
  const [basketX, setBasketX] = useState(50); // percentage
  const containerRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const requestRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
      setTimeLeft(t => Math.max(0, t - 1));
    }, 1000);

    const spawner = setInterval(() => {
        setItems(prev => [
            ...prev,
            {
                id: Date.now(),
                x: Math.random() * 90, // Keep within bounds
                y: -10,
                type: Math.random() > 0.8 ? 'bomb' : 'star'
            }
        ]);
    }, 800);

    return () => {
        clearInterval(timer);
        clearInterval(spawner);
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [isActive]);

  useEffect(() => {
    if (timeLeft === 0 && isActive) {
        setIsActive(false);
        onComplete(score);
    }
  }, [timeLeft, isActive, onComplete, score]);

  // Game Loop
  useEffect(() => {
      if (!isActive) return;

      const updateGame = () => {
          setItems(prev => {
              const nextItems = prev.map(item => ({ ...item, y: item.y + 1.5 })).filter(item => item.y < 105);
              
              // Collision detection
              // Simplified: checking if basketX is close to item.x when item.y is near bottom
              const caughtItems = nextItems.filter(item => (
                  item.y > 85 && item.y < 95 && 
                  Math.abs(item.x - basketX) < 15 // Tolerance
              ));

              caughtItems.forEach(item => {
                  if (item.type === 'star') {
                      setScore(s => s + 5);
                  } else {
                      setScore(s => Math.max(0, s - 10));
                  }
                  // Remove caught item
                  item.y = 200; // Force remove filter next frame
              });
              
              return nextItems.filter(item => item.y < 110);
          });
          
          requestRef.current = requestAnimationFrame(updateGame);
      };

      requestRef.current = requestAnimationFrame(updateGame);
      return () => {
          if (requestRef.current) cancelAnimationFrame(requestRef.current);
      }
  }, [isActive, basketX]);

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
      if (!containerRef.current) return;
      
      let clientX;
      if ('touches' in e) {
          clientX = e.touches[0].clientX;
      } else {
          clientX = (e as React.MouseEvent).clientX;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      setBasketX(Math.min(95, Math.max(5, x))); // Clamp
  };

  return (
    <div 
        className="flex flex-col items-center h-full w-full relative overflow-hidden touch-none"
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onTouchMove={handleMouseMove} // Touch support
    >
       <div className="absolute top-4 left-4 right-4 flex justify-between z-10 text-foreground font-bold text-xl pointer-events-none">
           <span>⏳ {timeLeft}</span>
           <span className="flex items-center gap-1"><Glyph name="star" size={20} /> {score}</span>
       </div>

       {!isActive ? (
           <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/80 z-20">
               <h2 className="text-3xl font-bold text-foreground mb-8">תפוס את הכוכבים</h2>
               <p className="text-muted-foreground mb-8">הזז את הסל כדי לתפוס כוכבים ולהימנע מפצצות!</p>
               <button 
                  onClick={() => { setScore(0); setTimeLeft(30); setIsActive(true); }}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black px-12 py-4 rounded-full font-bold text-xl shadow-lg"
               >
                  התחל
               </button>
           </div>
       ) : (
           <>
              {items.map(item => (
                  <div 
                    key={item.id}
                    className="absolute transition-none"
                    style={{ left: `${item.x}%`, top: `${item.y}%` }}
                  >
                      {item.type === 'star' ? <Glyph name="star" size={36} /> : <Glyph name="bomb" size={38} />}
                  </div>
              ))}
              
              {/* Basket */}
              <div 
                 className="absolute bottom-4 w-16 h-8 bg-white/20 border-2 border-white rounded-b-2xl backdrop-blur-sm transition-none"
                 style={{ left: `calc(${basketX}% - 2rem)` }}
              />
           </>
       )}
    </div>
  );
}
